import React, { useState } from 'react';
import { signInWithGoogleRobust, signInWithEmail, signUpWithEmail, sendPasswordReset } from '../services/firebase';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showAuthType, setShowAuthType] = useState<'login' | 'signup' | null>(null);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleGoogleAuth = async (isSignUp: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      console.log(`${isSignUp ? 'Google新規登録' : 'Googleログイン'}開始...`);
      
      // Googleログインを実行
      console.log('🔍 Googleログインを実行');
      const result = await signInWithGoogleRobust();
      console.log('Googleログイン成功:', result);
      
      // ログイン成功後、少し遅延してからコールバックを実行
      setTimeout(() => {
        console.log('🔍 LoginModal - onLoginSuccess呼び出し:', { 
          onLoginSuccess: !!onLoginSuccess,
          type: typeof onLoginSuccess,
          functionName: onLoginSuccess?.name || 'anonymous'
        });
        if (onLoginSuccess) {
          console.log('🔍 LoginModal - onLoginSuccess実行中...');
          try {
            onLoginSuccess();
            console.log('🔍 LoginModal - onLoginSuccess実行完了');
          } catch (error) {
            console.error('🔍 LoginModal - onLoginSuccess実行エラー:', error);
          }
        } else {
          console.warn('🔍 LoginModal - onLoginSuccessが未定義です');
        }
        
        // 直接ページ遷移も試す
        console.log('🔄 LoginModal - 直接ページ遷移を試行');
        setTimeout(() => {
          window.location.href = window.location.origin + window.location.pathname;
        }, 100);
        
        onClose();
      }, 500); // 遅延を500msに増加
    } catch (error: any) {
      console.error('Googleログインエラー詳細:', error);
      console.error('エラーコード:', error.code);
      console.error('エラーメッセージ:', error.message);
      setError(isSignUp ? '新規登録できませんでした。' : 'ログインできません。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      if (showAuthType === 'signup') {
        console.log('メールサインアップ開始...');
        await signUpWithEmail(email, password);
        console.log('メールサインアップ成功');
      } else {
        console.log('メールログイン開始...');
        await signInWithEmail(email, password);
        console.log('メールログイン成功');
      }
      
      // ログイン成功後、少し遅延してからコールバックを実行
      setTimeout(() => {
        onLoginSuccess();
        onClose();
        // フォームをリセット
        setEmail('');
        setPassword('');
        setShowEmailForm(false);
        setShowAuthType(null);
      }, 100);
    } catch (error: any) {
      console.error('メール認証エラー:', error);
      const message = String(error?.message || '');
      if (showAuthType === 'signup') {
        setError(message.includes('Firebase:') ? '新規登録できませんでした。' : (message || '新規登録できませんでした。'));
      } else {
        setError(message.includes('Firebase:') ? 'ログインできません。' : (message || 'ログインできません。'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('メールアドレスを入力してください。');
      setResetSuccess(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setResetSuccess(null);
      await sendPasswordReset(email);
      setResetSuccess('パスワード再設定用のメールを送信しました。メールをご確認ください。');
    } catch (error: any) {
      console.error('パスワード再設定エラー:', error);
      setError(error?.message || 'パスワード再設定メールを送信できませんでした。');
    } finally {
      setIsLoading(false);
    }
  };

  const openResetForm = () => {
    setShowResetForm(true);
    setError(null);
    setResetSuccess(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {showResetForm
              ? 'パスワードの再設定'
              : showAuthType === 'login'
                ? 'ログイン'
                : showAuthType === 'signup'
                  ? '新規登録'
                  : 'ログインが必要です'}
          </h2>
          <p className="text-slate-600">
            {showResetForm
              ? '登録したメールアドレス宛に、再設定用のリンクをお送りします。'
              : showAuthType === 'login'
                ? 'アカウントにログインしてください'
                : showAuthType === 'signup'
                  ? '新しいアカウントを作成してください'
                  : '高解像度で保存するには、アカウントにログインしてください。'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-600 text-sm">{error}</p>
            {!showResetForm && showAuthType === 'login' && showEmailForm && (
              <button
                type="button"
                onClick={openResetForm}
                className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                パスワードを再設定する
              </button>
            )}
          </div>
        )}

        {resetSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-green-700 text-sm">{resetSuccess}</p>
          </div>
        )}

        {!showAuthType ? (
          <div className="space-y-4">
            {/* ログインボタン */}
            <button
              onClick={() => setShowAuthType('login')}
              className="w-full bg-blue-600 text-white rounded-lg px-4 py-3 font-medium hover:bg-blue-700 transition-colors"
            >
              🔑 ログイン
            </button>
            
            {/* 新規登録ボタン */}
            <button
              onClick={() => setShowAuthType('signup')}
              className="w-full bg-green-600 text-white rounded-lg px-4 py-3 font-medium hover:bg-green-700 transition-colors"
            >
              ✨ 新規登録
            </button>

            <button
              onClick={onClose}
              className="w-full text-gray-500 hover:text-gray-700 transition-colors mt-2"
            >
              キャンセル
            </button>
          </div>
        ) : showResetForm ? (
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-slate-700 mb-2">
                メールアドレス
              </label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white rounded-lg px-4 py-3 font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  送信中...
                </div>
              ) : (
                '再設定メールを送る'
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowResetForm(false);
                setResetSuccess(null);
                setError(null);
              }}
              className="w-full text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isLoading}
            >
              ← ログインに戻る
            </button>
          </form>
        ) : !showEmailForm ? (
          <div className="space-y-3">
            {/* Google認証ボタン */}
            <button
              onClick={() => handleGoogleAuth(showAuthType === 'signup')}
              disabled={isLoading}
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 flex items-center justify-center space-x-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-gray-700 font-medium">
                    Googleで{showAuthType === 'signup' ? '新規登録' : 'ログイン'}
                  </span>
                </>
              )}
            </button>

            {/* 区切り線 */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">または</span>
              </div>
            </div>

            {/* メール認証ボタン */}
            <button
              onClick={() => setShowEmailForm(true)}
              className="w-full bg-blue-600 text-white rounded-lg px-4 py-3 font-medium hover:bg-blue-700 transition-colors"
            >
              📧 メールアドレスで{showAuthType === 'signup' ? '新規登録' : 'ログイン'}
            </button>

            {/* 戻るボタン */}
            <button
              onClick={() => {
                setShowAuthType(null);
                setError(null);
              }}
              className="w-full text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← 戻る
            </button>
          </div>
        ) : (
          <div>
            {/* メール/パスワードフォーム */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  メールアドレス
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  パスワード {isSignUp && <span className="text-xs text-slate-500">(6文字以上)</span>}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  minLength={6}
                  disabled={isLoading}
                />
              </div>

              {showAuthType !== 'signup' && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={openResetForm}
                    className="text-sm text-blue-600 hover:text-blue-800"
                    disabled={isLoading}
                  >
                    パスワードをお忘れですか？
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white rounded-lg px-4 py-3 font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    処理中...
                  </div>
                ) : (
                  showAuthType === 'signup' ? 'アカウント作成' : 'ログイン'
                )}
              </button>

              {/* 戻るボタン */}
              <button
                type="button"
                onClick={() => {
                  setShowEmailForm(false);
                  setShowResetForm(false);
                  setResetSuccess(null);
                  setEmail('');
                  setPassword('');
                  setError(null);
                }}
                className="w-full text-gray-500 hover:text-gray-700 transition-colors"
                disabled={isLoading}
              >
                ← 戻る
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
